"use client";

import {
  calculateDiscountedPrice,
  YEARLY_DISCOUNT,
} from "../const/publicPricing";

// The `yearly-discount-percentage` experiment has ended; the 50% variant
// was rolled out to everyone. We keep the hook and its return shape so
// existing call sites (PricingCards, PricingTabs, PricingCard) and the
// backend `experimentVariant` payload continue to work unchanged.
export type PricingExperimentVariant = "50";

export interface PricingExperimentConfig {
  variant: PricingExperimentVariant;
  discountPercentage: number;
  showLineThrough: boolean;
  showSavingsAmount: boolean;
  showDiscountPercentage: boolean;
}

export const usePricingExperiment = (): PricingExperimentConfig => {
  return {
    variant: "50",
    discountPercentage: YEARLY_DISCOUNT,
    showLineThrough: false,
    showSavingsAmount: false,
    showDiscountPercentage: true,
  };
};

// Lives in const/publicPricing.ts so server-rendered marketing pages can
// compute the same yearly prices; re-exported for the existing call sites.
export { calculateDiscountedPrice };

export const calculateSavingsAmount = (
  originalPrice: number,
  discountedPrice: number
): number => {
  return originalPrice - discountedPrice;
};

export const calculateMaxYearlySavings = (
  monthlyPrices: number[],
  discountPercentage: number
): number => {
  const maxMonthlyPrice = Math.max(...monthlyPrices);
  const yearlyOriginalPrice = maxMonthlyPrice * 12;
  const yearlyDiscountedPrice = calculateDiscountedPrice(
    yearlyOriginalPrice,
    discountPercentage
  );
  return calculateSavingsAmount(yearlyOriginalPrice, yearlyDiscountedPrice);
};

// Helper function to get the experimental discount amount for checkout
export const getExperimentalDiscountAmount = (
  originalYearlyPrice: number,
  discountPercentage: number
): number => {
  const currentDiscountPercentage = 0.2; // Baseline 20% discount
  const currentDiscountedPrice = calculateDiscountedPrice(
    originalYearlyPrice,
    currentDiscountPercentage
  );
  const experimentalDiscountedPrice = calculateDiscountedPrice(
    originalYearlyPrice,
    discountPercentage
  );

  // Return the additional discount amount (can be negative if less discount)
  return currentDiscountedPrice - experimentalDiscountedPrice;
};
